<template>
  <div :class="['MetricCard', `MetricCard--trend-${trendTheme}`]">
    <div class="MetricCard__title">{{ title }}</div>
    <div class="MetricCard__content">
      <div v-if="subtitle" class="MetricCard__subtitle">{{ subtitle }}</div>
      <div v-if="trendText" class="MetricCard__trend">
        {{ trendText }}<span class="MetricCard__trend-vs"> vs last year</span>
      </div>
    </div>
    <img class="MetricCard__chart" :src="chartSrc" alt="" aria-hidden="true" loading="lazy" />
  </div>
</template>

<script setup lang="ts">
import { PropType } from 'vue'
import chartSrc from './assets/chart.avif'

defineProps({
  title: {
    type: String,
    required: true,
  },
  subtitle: {
    type: String,
    default: '',
  },
  trendText: {
    type: String,
    default: '',
  },
  trendTheme: {
    type: String as PropType<'success' | 'error' | 'neutral'>,
    default: 'success',
  },
  width: {
    type: String,
    default: '18.5rem',
  },
})
</script>

<style lang="scss" scoped>
.MetricCard {
  background: rgba(255, 255, 255, 0.95);
  border-radius: 0.5rem;
  padding: 0.75rem 1rem;
  width: v-bind(width);
  box-shadow: 0px 1px 2px 0px #0010350d;
  border: 1px solid $newcolor-grey-200;
  display: grid;
  grid-template-columns: 1fr auto; // text left, chart right
  grid-auto-rows: min-content;
  column-gap: 1rem;
  row-gap: 0.25rem;
  align-items: start;

  .MetricCard__content {
    min-width: 0; // allow text to wrap properly
    grid-column: 1; // left column
  }

  .MetricCard__title {
    @include font-size-30px();
    font-weight: 600;
    color: $color-ui-gray-cool-900;
    margin-bottom: 0.25rem;
    grid-column: 1 / -1; // span full width
  }

  .MetricCard__subtitle {
    @include font-size-16px();
    color: $color-ui-gray-cool-600;
    font-weight: 600;
    margin-bottom: 0.5rem;
  }

  .MetricCard__trend {
    @include font-size-14px();
    font-weight: 600;
    white-space: nowrap;

    .MetricCard__trend-vs {
      @include font-size-14px();
      color: $color-ui-gray-cool-600;
      font-weight: 500;
    }
  }

  .MetricCard__chart {
    width: 7.5rem;
    height: auto;
    grid-column: 2; // right column next to subtitle
    align-self: end;
    justify-self: end;
    pointer-events: none;
    user-select: none;
    opacity: 0.95;
    // Left-edge fade similar to carousel/trust components
    --fade-left-size: 56px;
    -webkit-mask-image: linear-gradient(
      to right,
      rgba(0, 0, 0, 0) 0,
      rgba(0, 0, 0, 1) var(--fade-left-size),
      rgba(0, 0, 0, 1) 100%
    );
    mask-image: linear-gradient(
      to right,
      rgba(0, 0, 0, 0) 0,
      rgba(0, 0, 0, 1) var(--fade-left-size),
      rgba(0, 0, 0, 1) 100%
    );
  }

  &.MetricCard--trend-success {
    .MetricCard__trend {
      color: $color-ui-success-main;
    }
  }

  &.MetricCard--trend-error {
    .MetricCard__trend {
      color: $color-ui-error-600;
    }
  }

  &.MetricCard--trend-neutral {
    .MetricCard__trend {
      color: $color-ui-gray-cool-700;
    }
  }
}
</style>
